/**
 * SendWhatsAppButton — Iter 93 (Feb 2026)
 *
 * Botón reutilizable "📱 Enviar por WA" que abre un mini-diálogo con
 * título + prioridad + mensaje + URL opcional y llama al endpoint genérico
 * `POST /api/whatsapp/notify`.
 *
 * Props:
 *   - to (str, requerido): número WA del destinatario (con o sin +)
 *   - nombreDestinatario (str, opcional): nombre que se inyecta en la plantilla
 *   - contextoDefault (str, opcional): texto pre-rellenado en el mensaje
 *     (ej: "Sobre la petición DNF-2026-0123")
 *   - urlDefault (str, opcional): URL pre-rellenada para el CTA
 *   - size (str): "sm" (default) | "xs"
 *   - variant (str): "outline" (default) | "ghost"
 *   - label (str): texto del botón. Si no, "Enviar WA"
 *   - iconOnly (bool): si true, solo muestra el icono (modo compacto)
 *
 * Uso:
 *   <SendWhatsAppButton to={cliente.telefono} nombreDestinatario={cliente.nombre} />
 */
import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { MessageCircle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PRIORIDADES = [
  { value: "urgente", label: "🔴 Urgente" },
  { value: "alta",    label: "🟠 Alta" },
  { value: "media",   label: "🟡 Media" },
  { value: "info",    label: "🔵 Info" },
  { value: "prueba",  label: "⚙️ Prueba" },
];

export function SendWhatsAppButton({
  to,
  nombreDestinatario = "",
  contextoDefault = "",
  urlDefault = "",
  size = "sm",
  variant = "outline",
  label,
  iconOnly = false,
  testId = "send-wa-btn",
}) {
  const [open, setOpen] = useState(false);
  const [titulo, setTitulo] = useState("");
  const [prioridad, setPrioridad] = useState("media");
  const [mensaje, setMensaje] = useState(contextoDefault);
  const [url, setUrl] = useState(urlDefault);
  const [sending, setSending] = useState(false);

  if (!to) return null;  // sin teléfono no se renderiza

  const reset = () => {
    setTitulo("");
    setPrioridad("media");
    setMensaje(contextoDefault);
    setUrl(urlDefault);
  };

  const onOpenChange = (o) => {
    setOpen(o);
    if (!o) reset();
  };

  const enviar = async () => {
    if (!titulo.trim()) {
      toast.error("Indica un título");
      return;
    }
    setSending(true);
    try {
      await api.post("/whatsapp/notify", {
        to,
        nombre: (nombreDestinatario || "").split(" ")[0] || nombreDestinatario,
        titulo: titulo.trim(),
        prioridad,
        mensaje: mensaje.trim() || null,
        url: (url || "").trim() || null,
      });
      toast.success(`Mensaje WhatsApp enviado a ${nombreDestinatario || to}`);
      setOpen(false);
      reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error enviando WhatsApp");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        size={size === "xs" ? "sm" : size}
        variant={variant}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        data-testid={testId}
        title={`Enviar WhatsApp a ${nombreDestinatario || to}`}
        className={size === "xs" ? "h-7 text-[11px] px-2" : ""}
      >
        <MessageCircle className={iconOnly ? "w-3.5 h-3.5" : "w-3.5 h-3.5 mr-1.5"} />
        {!iconOnly && (label || "Enviar WA")}
      </Button>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-zinc-950 border-emerald-500/40 max-w-md"
                       data-testid="send-wa-dialog">
          <DialogHeader>
            <DialogTitle className="text-emerald-200 flex items-center gap-2">
              <MessageCircle className="w-5 h-5" /> Enviar por WhatsApp
            </DialogTitle>
            <div className="text-xs text-zinc-400 pt-1">
              Destinatario: <span className="text-zinc-100 font-medium">{nombreDestinatario || "—"}</span>
              {" · "}
              <span className="font-mono text-emerald-300">{to}</span>
            </div>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs text-zinc-400">Título * <span className="text-zinc-600">(asunto del mensaje)</span></Label>
              <Input
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Ej: Llamada urgente"
                maxLength={80}
                data-testid="send-wa-titulo"
                className="bg-zinc-900 border-zinc-800 mt-1"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Prioridad</Label>
              <Select value={prioridad} onValueChange={setPrioridad}>
                <SelectTrigger data-testid="send-wa-prioridad"
                               className="bg-zinc-900 border-zinc-800 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  {PRIORIDADES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Mensaje <span className="text-zinc-600">(opcional, se concatena al título)</span></Label>
              <Textarea
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                placeholder="Detalle adicional…"
                rows={3}
                maxLength={500}
                data-testid="send-wa-mensaje"
                className="bg-zinc-900 border-zinc-800 mt-1 resize-none"
              />
              <div className="text-[10px] text-zinc-500 mt-0.5">{mensaje.length}/500</div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">URL <span className="text-zinc-600">(opcional, botón CTA del mensaje)</span></Label>
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://app.rotulaciondnf.com/…"
                data-testid="send-wa-url"
                className="bg-zinc-900 border-zinc-800 mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}
                    data-testid="send-wa-cancel">Cancelar</Button>
            <Button onClick={enviar} disabled={sending || !titulo.trim()}
                    data-testid="send-wa-send"
                    className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold">
              {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SendWhatsAppButton;
